import React, { ReactNode } from 'react';

import { Chip, Link, Paper, Stack, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';

import { ConfigurationCost, DepartureCostSummary } from './departureInventory';
import { formatAmount } from './formatAmount';
import { withProfitShare } from './profitShare';

export interface SupplierCost {
  key: string;
  /** Omitted where the page already names the one supplier. */
  name?: ReactNode;
  summary: DepartureCostSummary;
  /** The supplier's share on top of the base cost, set only where it has a profit share rate. */
  profitShare?: number | null;
  profitShareRate?: number | null;
  /** The supplier's total, profit share included, in AUD. */
  audCost?: number;
  isConverting?: boolean;
  onSelect?: () => void;
}

interface Props {
  suppliers: Array<SupplierCost>;
  /** Everyone on the departure, counted once however many suppliers carry them. */
  travellers: { units: number; singles: number };
  note?: ReactNode;
  /** Inside a card that already headlines the total, so only the sum is shown. */
  embedded?: boolean;
}

interface Line {
  key: string;
  label: string;
  calculation: string;
  detail?: string;
  amount: number | null;
}

function linesFor(configuration: ConfigurationCost): Array<Line> {
  const {
    packageConfigurationId: key,
    label,
    unitPrice,
    cost,
    singleSupplementCost,
    upgradeTravellers,
  } = configuration;
  const units = configuration.units + upgradeTravellers.units;
  const singles = configuration.singles + upgradeTravellers.singles;

  if (configuration.isUnpriced) {
    return [{ key, label, calculation: `${units} × no price`, amount: null }];
  }

  if (cost === null || !units) {
    return [];
  }

  return [
    {
      key,
      label,
      calculation: `${units} × ${formatAmount(unitPrice)}`,
      detail: configuration.isBeyondScale ? `largest quoted rate (${configuration.pricedAtPax} pax)` : undefined,
      amount: cost - (singleSupplementCost ?? 0),
    },
    ...(singles && singleSupplementCost
      ? [
          {
            key: `${key}:singles`,
            label: `${label} single supplement`,
            calculation: `${singles} × ${formatAmount(configuration.singleSupplement)}`,
            amount: singleSupplementCost,
          },
        ]
      : []),
  ];
}

const hasMissingCost = (summary: DepartureCostSummary | undefined) =>
  !!summary?.configurations.some((configuration) => configuration.isUnpriced);

const aud = (amount: number | undefined, isConverting?: boolean) =>
  isConverting ? 'Converting…' : amount === undefined ? 'AUD unavailable' : `$${formatAmount(amount)} AUD`;

/** The departure's supplier cost, laid out as the sum that produces it. */
export default function CostBreakdown({ suppliers, travellers, note, embedded = false }: Props) {
  const priced = suppliers.filter(({ summary }) => summary.totalCost !== null);
  const isConverting = priced.some((supplier) => supplier.isConverting);
  const audTotal =
    priced.length && priced.every((supplier) => supplier.audCost !== undefined)
      ? priced.reduce((total, supplier) => total + supplier.audCost, 0)
      : undefined;
  const isMissingCost = suppliers.some(({ summary }) => hasMissingCost(summary));

  const table = (
    <Table size="small" sx={{ mt: embedded ? 0 : 2, '& td': { px: 0 }, '& td:not(:first-of-type)': { pl: 2 } }}>
      <TableBody>
        {suppliers.map(
          ({ key, name, summary, profitShare, profitShareRate, audCost, isConverting: converting, onSelect }) => {
            const lines = summary.configurations.flatMap(linesFor);
            const totalCost = withProfitShare(summary.totalCost, profitShare);
            const isMissing = hasMissingCost(summary);

            return (
              <React.Fragment key={key}>
                {!!name && (
                  <TableRow>
                    <TableCell colSpan={3} sx={{ pt: 2 }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {onSelect ? (
                          <Link component="button" underline="hover" color="inherit" onClick={onSelect}>
                            {name}
                          </Link>
                        ) : (
                          name
                        )}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {!lines.length && (
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Typography variant="body2" color="text.secondary">
                        No travellers on this departure
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {lines.map((line) => (
                  <TableRow key={line.key}>
                    <TableCell>
                      <Typography variant="body2">{line.label}</Typography>
                      {!!line.detail && (
                        <Typography variant="caption" color="text.secondary">
                          {line.detail}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" color="text.secondary">
                        {line.calculation}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {line.amount === null ? (
                        <Chip size="small" color="warning" label="No price" />
                      ) : (
                        formatAmount(line.amount)
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {profitShare !== undefined && !!profitShareRate && summary.totalCost !== null && (
                  <>
                    <TableRow>
                      <TableCell>
                        <Typography variant="body2">Base cost</Typography>
                      </TableCell>
                      <TableCell />
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {formatAmount(summary.totalCost)} {summary.currency}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <Typography variant="body2">Profit share</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" color="text.secondary">
                          {formatAmount(summary.totalCost)} ÷ {(1 - profitShareRate).toFixed(2)} −{' '}
                          {formatAmount(summary.totalCost)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {formatAmount(profitShare)} {summary.currency}
                      </TableCell>
                    </TableRow>
                  </>
                )}
                {(totalCost !== null || isMissing) && (
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {suppliers.length > 1 ? 'Subtotal' : 'Total'}
                      </Typography>
                    </TableCell>
                    <TableCell />
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {totalCost === null ? (
                        <Chip size="small" color="warning" label="Missing cost" />
                      ) : (
                        <>
                          <Typography variant="body2" fontWeight={600}>
                            {formatAmount(totalCost)} {summary.currency}
                          </Typography>
                          {isMissing && (
                            <Typography variant="caption" color="warning.main" display="block">
                              excludes options with no price
                            </Typography>
                          )}
                          {summary.currency !== 'AUD' && (
                            <Typography variant="caption" color="text.secondary">
                              {aud(audCost, converting)}
                            </Typography>
                          )}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          },
        )}
      </TableBody>
    </Table>
  );

  if (embedded) {
    return table;
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={0.5}>
        <Typography variant="caption" color="text.secondary">
          Total cost
        </Typography>
        <Typography variant="h4" fontWeight={600}>
          {aud(audTotal, isConverting)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {travellers.units} travellers · {travellers.singles} single
          {note && <> · {note}</>}
        </Typography>
        {isMissingCost && (
          <Typography variant="caption" color="warning.main">
            Incomplete: some booked options have no cost price, so they are not in this total
          </Typography>
        )}
      </Stack>

      {table}
    </Paper>
  );
}
